/**
 * User profile management API functions
 */

export interface UserProfileData {
  firstName?: string
  lastName?: string
  profilePicture?: string
}

export interface UserProfileResponse {
  success: boolean
  message: string
  data?: UserProfileData
}

/**
 * Update user profile data (first name, last name)
 */
export async function updateUserProfile(profileData: UserProfileData): Promise<UserProfileResponse> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')

  const url = `${config.restApiUrl}kj-cockpit/v1/user-profile`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-WP-Nonce': config.restNonce
  }

  const isDevMode = config.devMode || false
  if (isDevMode) {
    console.log('[updateUserProfile] Making request to:', url)
    console.log('[updateUserProfile] Profile data:', profileData)
  }

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({
      firstName: profileData.firstName,
      lastName: profileData.lastName
    })
  })

  if (!res.ok) {
    throw new Error(`Failed to update profile: ${res.status}`)
  }

  const response = await res.json()
  
  if (isDevMode) {
    console.log('[updateUserProfile] Response:', response)
  }

  return response
}

/**
 * Upload profile picture
 */
export async function uploadProfilePicture(file: File): Promise<UserProfileResponse> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')

  const url = `${config.restApiUrl}kj-cockpit/v1/user-profile/picture`

  const formData = new FormData()
  formData.append('profile_picture', file)

  const isDevMode = config.devMode || false
  if (isDevMode) {
    console.log('[uploadProfilePicture] Making request to:', url)
    console.log('[uploadProfilePicture] File:', file.name, file.size, 'bytes')
  }

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': config.restNonce
    },
    body: formData
  })

  if (!res.ok) {
    throw new Error(`Failed to upload profile picture: ${res.status}`)
  }

  const response = await res.json()
  
  if (isDevMode) {
    console.log('[uploadProfilePicture] Response:', response)
  }

  return response
}

/**
 * Get current user profile data
 */
export async function getUserProfile(): Promise<UserProfileData> {
  const config = (window as any).WP_COCKPIT
  if (!config) throw new Error('WP Cockpit config not available')

  const url = `${config.restApiUrl}kj-cockpit/v1/user-profile`

  const isDevMode = config.devMode || false
  if (isDevMode) {
    console.log('[getUserProfile] Making request to:', url)
  }

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': config.restNonce
    }
  })

  if (!res.ok) {
    throw new Error(`Failed to get user profile: ${res.status}`)
  }

  const response = await res.json()
  
  if (isDevMode) {
    console.log('[getUserProfile] Response:', response)
  }
  
  if (response.success) {
    return response.data || {}
  } else {
    throw new Error(response.message || 'Failed to get user profile')
  }
}